import React, { useEffect, useState } from "react";
import axios from "axios";

import { getFileUrl } from "../../../core/config/api";
import ListingList from "../../../components/listing/List";
import { Button, ButtonGroup, Icon, IconButton } from "../../../components/ui";
import SelectableListingCard from "../../../order/components/QuoteActions/SelectListing/SelectableListingCard";
import { useUpsertQuote } from "../../../order/hooks/quotes";
import { useRequestContext } from "../../hooks/useRequest";

// V2 "Listings" section. Two states:
//   1. No listing linked → linker UI (create new from quote OR pick
//      from catalog). Unblocks Payments for orders born from PriceIQ.
//   2. Listing linked → summary card with × remove button. Removal is
//      blocked at the backend when any deposit references the listing.
//
// Slot-aware: when `step` is provided, getContext(idx, step) resolves
// the slot's Request (primary or additionalMainRequests[mainSlotIdx-1])
// and writes back via slotSetRequest. Without step, falls back to the
// singular `request` state — preserves single-main behaviour.
export function ConnectListing({
  step = null,
  idx = 0,
  selectedQuote = null,
  onAdvance,
} = {}) {
  const ctx = useRequestContext();
  const {
    getContext,
    refreshRequest,
    slotSetRequest,
    request: primaryRequest,
    product: primaryProduct,
  } = ctx;
  const slotCtx = step ? getContext(idx, step) : null;
  const request = slotCtx?.request ?? primaryRequest;
  const product = slotCtx?.product ?? primaryProduct;
  const refreshSlot = async () => {
    if (slotCtx && step?.mainSlotIdx > 0 && request?.id) {
      // Additional main slot — fetch the freshest Request and push
      // it into additionalMainRequests[arrIdx] so the slot's quote /
      // listing relationship updates locally without clobbering the
      // primary's state.
      try {
        const { data } = await axios.get(`/api/requests/${request.id}`);
        slotSetRequest?.(step, data);
      } catch (e) {
        // fall back to primary refresh — harmless for the slot's view
        await refreshRequest?.();
      }
      return;
    }
    await refreshRequest?.();
  };
  const { upsertQuote } = useUpsertQuote();

  const quote = selectedQuote ?? request?.last_quote;
  const productId = product?.id ?? quote?.product_id ?? request?.product_id;
  // Two paths can attach a listing:
  //   1. Active Listings tab on Search Product → writes
  //      request.linked_listing_id (no quote needed).
  //   2. This section's "create / pick" flow → writes
  //      quote.listing_id (requires a quote).
  // Accept either as "linked" so the connected-state card shows up
  // immediately after either path, regardless of which the rep used.
  const currentListingId =
    quote?.listing_id ?? request?.linked_listing_id ?? null;

  const [selected, setSelected] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [detachError, setDetachError] = useState(null);
  const [linkedListing, setLinkedListing] = useState(null);

  // Fetch the linked listing's full data for the summary card.
  useEffect(() => {
    if (!currentListingId) {
      setLinkedListing(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/listings/${currentListingId}`)
      .then(({ data }) => {
        if (!cancelled) setLinkedListing(data);
      })
      .catch(() => {
        if (!cancelled) setLinkedListing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentListingId]);

  // Connected state via tab path can happen before a quote exists
  // (Active Listings tab writes request.linked_listing_id directly).
  // Only block when neither a listing nor a quote is in scope.
  if (!quote?.id && !currentListingId) {
    return (
      <div className="rc-request-v2__view-empty">
        Confirm the request and create a quote first.
      </div>
    );
  }
  if (!productId) {
    return (
      <div className="rc-request-v2__view-empty">
        Missing product — cannot list candidate listings.
      </div>
    );
  }

  const effectiveSelectedId = selected?.id ?? currentListingId;
  const hasChanged = !!selected && selected.id !== currentListingId;

  const onConfirm = async () => {
    if (!hasChanged) return;
    setIsSaving(true);
    try {
      await upsertQuote({
        id: quote.id,
        quoteData: { ...quote, listing_id: selected.id },
      });
      await refreshSlot();
      setSelected(null);
    } finally {
      setIsSaving(false);
    }
  };

  const onCreateFromQuote = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await axios.post(`/api/quotes/${quote.id}/create-listing`);
      await refreshSlot();
    } catch (e) {
      setCreateError(
        e?.response?.data?.message ||
          "Could not create listing — please try again or contact support.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const onDetach = async () => {
    setIsDetaching(true);
    setDetachError(null);
    try {
      // Detach is request-side because the listing may be linked via
      // request.linked_listing_id (Active Listings tab) OR
      // quote.listing_id (this section's linker). The backend nulls
      // whichever applies.
      await axios.post(`/api/requests/${request.id}/detach-listing`);
      await refreshSlot();
    } catch (e) {
      // Laravel ValidationException returns 422 with { errors: { ... } }
      const errs = e?.response?.data?.errors;
      const firstErr = errs ? Object.values(errs).flat()[0] : null;
      setDetachError(
        firstErr ||
          e?.response?.data?.message ||
          "Could not remove the listing — please try again.",
      );
    } finally {
      setIsDetaching(false);
    }
  };

  // ─── CONNECTED STATE — canonical .listing.vertical card ───────────
  // Uses the same DOM as the Create Listing / Search Product catalog
  // grid (V2SearchProduct.jsx::RCVerticalCard) so the look is
  // identical. Wrapped in .find-product-product so rc-request.scss's
  // .listing.vertical rules pick up.
  if (currentListingId && linkedListing) {
    const p = linkedListing.product || {};
    const thumbId =
      p.images?.[0]?.id ?? linkedListing.images?.[0]?.id ?? null;
    const imageUrl = thumbId ? getFileUrl(thumbId) : null;
    const descBits = [
      linkedListing.year,
      linkedListing.condition,
      linkedListing.buying_price
        ? `${(linkedListing.buying_currency || "gbp").toUpperCase()} ${(linkedListing.buying_price / 100).toLocaleString()}`
        : null,
      linkedListing.supplier,
    ].filter(Boolean);
    const desc = descBits.join(" / ");
    return (
      <div className="rc-request-v2__connect-listing">
        <div className="rc-request-v2__linked-card">
          <IconButton
            size="sm"
            round
            label="Remove listing"
            title="Remove listing"
            className="rc-request-v2__listing-card-remove"
            onClick={onDetach}
            loading={isDetaching}
            data-btn="listing-connect-delete"
          >
            <Icon name="X" size="sm" />
          </IconButton>
          <div className="rc-request-v2__linked-card-image">
            {imageUrl ? (
              <img src={imageUrl} alt={p.model?.name || p.brand?.name || ""} />
            ) : (
              <div className="rc-request-v2__linked-card-noimage" />
            )}
          </div>
          <div className="rc-request-v2__linked-card-body">
            <div className="rc-request-v2__linked-card-header">
              <span className="rc-request-v2__linked-card-ref">
                {p.reference_number || "—"}
              </span>
              <span className="rc-request-v2__linked-card-brand">
                {p.brand?.name || ""}
              </span>
            </div>
            <div className="rc-request-v2__linked-card-model">
              {p.model?.name || ""}
            </div>
            {desc && (
              <div className="rc-request-v2__linked-card-desc">{desc}</div>
            )}
          </div>
        </div>
        {detachError && (
          <div className="rc-request-v2__connect-listing-error">{detachError}</div>
        )}
      </div>
    );
  }

  // ─── EMPTY STATE — show the linker (create / pick) ────────────────
  return (
    <div className="rc-request-v2__connect-listing">
      <div className="rc-request-v2__connect-listing-actions">
        <Button
          variant="secondary"
          fullWidth
          disabled={isSaving}
          loading={isCreating}
          onClick={onCreateFromQuote}
          data-btn="listing-create"
        >
          Create new listing from this quote
        </Button>
        {createError && (
          <div className="rc-request-v2__connect-listing-error">{createError}</div>
        )}
      </div>

      <div className="rc-request-v2__connect-listing-or">
        or pick an existing listing for this product
      </div>

      <ListingList
        list="product"
        filters={{ product_id: productId }}
        itemComponent={SelectableListingCard}
        itemComponentProps={{
          selectedId: effectiveSelectedId,
          currentListingId,
          productId,
          onSelect: setSelected,
        }}
        limit={48}
        firstPageOnly
      />

      <ButtonGroup align="end" className="rc-request-v2__connect-listing-footer">
        {onAdvance && (
          <Button
            variant="ghost"
            onClick={() => onAdvance()}
            disabled={isSaving || isCreating}
            data-btn="listing-connect-cancel"
          >
            Skip
          </Button>
        )}
        <Button
          variant="primary"
          disabled={!hasChanged || isCreating}
          loading={isSaving}
          onClick={onConfirm}
          data-btn="listing-connect-confirm"
        >
          {hasChanged ? "Connect listing" : "Select a listing"}
        </Button>
      </ButtonGroup>
    </div>
  );
}
